// react core
import { Fragment, useContext, useState } from "react";

// date handling
import dayjs from "dayjs";

// material design
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// entzy context and services
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { TextTag, HeaderMenuTag } from "components/utils/common/CommonTags";
import {
  ActionLoader,
  ActionCheck,
} from "components/utils/common/CommonLoaders";
import { BigButton, MoreButton } from "components/utils/common/CommonButtons";
import { UserAvatar } from "components/utils/common/CommonAvatars";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessageCaptions as iconMessages } from "@fortawesome/pro-thin-svg-icons";
import { faRocketLaunch as iconActvity } from "@fortawesome/pro-thin-svg-icons";
import { faInfoCircle as iconInfo } from "@fortawesome/pro-duotone-svg-icons";
import { faCheck as iconRead } from "@fortawesome/pro-thin-svg-icons";
import { faEdit as iconEdit } from "@fortawesome/pro-duotone-svg-icons";
import { faMessages as iconOpenDMs } from "@fortawesome/pro-duotone-svg-icons";
import { faExternalLink as iconOpenUrl } from "@fortawesome/pro-solid-svg-icons";
import { faClose as iconClose } from "@fortawesome/pro-thin-svg-icons";

function MainFeed(props) {
  // const user = props.user;
  const navigate = props.navigate;

  const feeds = {
    messages: {
      id: "messages",
      name: "Messages",
      icon: iconMessages,
    },
    activity: {
      id: "activity",
      name: "Activity",
      icon: iconActvity,
    },
  };
  const feedView = props.feedView ? props.feedView : "messages";

  const mainContext = useContext(MainContext);

  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [filterView, setFilterView] = useState("all");

  const [dialog, setDialog] = useState({
    visible: false,
  });

  const handleFilterView = (view) => {
    setFilterView(view);
  };

  const handleNavigateEvent = (item, url) => {
    if (url) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (!url.startsWith("http")) {
        mainContext.updateViewerTrail({
          menuSourcePage: feedView,
        });
        mainContext.selectMenu("e");
        handleMarkRead(item, true);
        navigate(url);
      }
    }
  };

  const handleEditing = () => {
    if (feedView === feeds.messages.id) {
      mainContext.updateViewerTrail({
        editingMessageFeed: !mainContext.state.viewer.trail.editingMessageFeed,
      });
    }
    if (feedView === feeds.activity.id) {
      mainContext.updateViewerTrail({
        editingActivityFeed:
          !mainContext.state.viewer.trail.editingActivityFeed,
      });
    }
  };

  const handleMarkRead = async (notification, readStatus) => {
    setLoading(true);
    setLoadingId(notification.NotificationId);
    const response = await mainContext.prepareUpdateNotification({
      notification: notification,
      action: readStatus ? "markread" : "markunread",
    });
    if (!response.alert) {
      mainContext.updateNotification(response);
    }
    setLoading(false);
    setLoadingId(null);
    return response;
  };
  const handleMarkAllRead = async (notifications, readStatus) => {
    setLoading(true);
    const promises = notifications.map(async (notification) => {
      const response = await mainContext.prepareUpdateNotification({
        notification: notification,
        action: readStatus ? "markread" : "markunread",
      });
      if (!response.alert) {
        mainContext.updateNotification(response);
      }
    });
    await Promise.all(promises);
    setLoading(false);
    return true;
  };

  const handleRemove = async (notification) => {
    setLoading(true);
    setLoadingId(notification.NotificationId);
    const response = await mainContext.prepareUpdateNotification({
      notification: notification,
      action: "remove",
    });
    if (!response.alert) {
      mainContext.updateNotification(response);
    }
    setLoading(false);
    setLoadingId(null);
    return response;
  };
  const handleRemoveAll = async (notifications) => {
    setLoading(true);
    const promises = notifications.map(async (notification) => {
      const response = await mainContext.prepareUpdateNotification({
        notification: notification,
        action: "remove",
      });
      if (!response.alert) {
        mainContext.updateNotification(response);
      }
    });
    await Promise.all(promises);
    setLoading(false);
    return true;
  };
  const handleRemoveConfirm = async (event, notification, confirm) => {
    if (confirm === true) {
      setDialog({ visible: false });
      await handleRemove(notification);
    } else if (confirm === false) {
      setDialog({ visible: false });
    } else {
      setDialog({
        visible: true,
        title: "Confirm Remove Alert",
        description:
          "Are you sure you want to remove this notification alert from your feed? This action cannot be undone.",
        cancel: (e) => handleRemoveConfirm(e, notification, false),
        confirm: (e) => handleRemoveConfirm(e, notification, true),
      });
    }
  };
  const handleRemoveAllConfirm = async (event, notifications, confirm) => {
    if (confirm === true) {
      setDialog({ visible: false });
      await handleRemoveAll(notifications);
    } else if (confirm === false) {
      setDialog({ visible: false });
    } else {
      setDialog({
        visible: true,
        title: "Confirm Remove All Alerts",
        description:
          "Are you sure you want to remove all notification alerts from this feed? This action cannot be undone.",
        cancel: (e) => handleRemoveAllConfirm(e, notifications, false),
        confirm: (e) => handleRemoveAllConfirm(e, notifications, true),
      });
    }
  };

  const handleRefresh = async () => {
    const response = await Promise.all([
      mainContext.preparePullNotifications({
        Category: null,
      }),
      // mainContext.preparePullViewerEvents({
      //   nextToken: null,
      // }),
    ]);
    if (response.filter((item) => item.alert).length > 0) {
      mainContext.updateAlert({
        alert: true,
        message:
          "Hmm unable to refresh content on that try. Give it another go or contact us if this problem persists.",
      });
      return true;
    } else {
      mainContext.pullNotifications(response[0]);
      // mainContext.pullViewerEvents(response[1]);
      return true;
    }
  };

  const handlePullMore = async () => {
    setLoadingMore(true);
    const response = await mainContext.preparePullNotifications({
      nextToken: mainContext.state.notifications.data.nextToken,
      more: true,
    });
    if (response.alert) {
      mainContext.updateAlert(response);
    } else {
      mainContext.pullNotifications(response);
    }
    setLoadingMore(false);
  };

  const NotificationList = (notifyProps) => {
    return (
      <Box
        className="box-default"
        sx={{
          px: configEntzy.APP_SPACING_SM,
        }}
      >
        {notifyProps.notifications.map((item, index) => {
          let internalUrl = item.MessageUrl.replace(configEntzy.APP_URL, "")
            .replace(configEntzy.APP_LONG_URL, "")
            .replace(configEntzy.APP_LOCAL_URL, "")
            .replace(configEntzy.APP_LOCAL_CLOUD_URL, "")
            .replace("/@", "@");
          let displayUrl = internalUrl;
          let memberDirectMessage = false;
          if (internalUrl.startsWith("@")) {
            internalUrl = internalUrl.replace("@", "/@/");
            memberDirectMessage = true;
          }
          if (!memberDirectMessage) {
            if (item.MessageCategory.startsWith("message")) {
              // check if chat and room name already present
              if (!internalUrl.includes("/chat")) {
                internalUrl += "/chat";
              }
              // remove from display url anything after /chat
              displayUrl = displayUrl.split("/chat")[0];
            }
            if (item.MessageCategory.startsWith("activity")) {
              if (item.MessageCategory.endsWith("rider")) {
                internalUrl += "/riders";
              } else {
                internalUrl += "/eventuate";
              }
            }
          }

          // let isMessage =
          //   item.MessageCategory && item.MessageCategory.startsWith("message");
          let daysAgo = dayjs().diff(dayjs(item.LastUpdated), "days");
          return (
            <Box
              key={item.NotificationId}
              className="box-default"
              sx={{
                pb: configEntzy.APP_SPACING_MD,
              }}
            >
              <Box
                className="box-default text-left bg-black-t50 shadow-default"
                sx={{
                  minWidth: configEntzy.AVATAR_SIZE_MD,
                  borderRadius: configEntzy.BORDER_SIZE_XL,
                  borderStyle: "solid",
                  borderWidth: item.MessageRead ? "1px" : "1px",
                  borderColor: item.MessageRead ? "#111" : "purple",
                  // backgroundColor: item.MessageRead ? "transparent" : "black",
                  color: item.MessageRead ? "#555" : "#999",
                }}
              >
                {/* ----- title theme and url click ----- */}
                <Box
                  className={
                    item.MessageRead
                      ? "box-default"
                      : "box-default bg-black-t50"
                  }
                  sx={{
                    px: configEntzy.APP_SPACING_MD2X,
                    py: configEntzy.APP_SPACING_MD,
                    // backgroundColor: item.MessageRead ? "transparent" : "red",
                    color: item.MessageRead ? undefined : "orange",
                    borderTopLeftRadius: configEntzy.BORDER_SIZE_XL,
                    borderTopRightRadius: configEntzy.BORDER_SIZE_XL,
                  }}
                >
                  <Box
                    className="box-default action-pointer"
                    onClick={() => {
                      handleNavigateEvent(item, internalUrl);
                    }}
                  >
                    <Typography variant="h6" noWrap={true}>
                      <span>{displayUrl}&nbsp;&nbsp;</span>
                      <span style={{ float: "right" }}>
                        <FontAwesomeIcon icon={iconOpenUrl} />
                      </span>
                    </Typography>
                  </Box>
                </Box>

                {/* ----- section divider ----- */}
                <Box
                  className="box-default"
                  sx={{
                    // pt: configEntzy.APP_SPACING_MD,
                    // pb: configEntzy.APP_SPACING_MD,
                    opacity: 0.5,
                  }}
                >
                  <Divider
                    sx={{
                      backgroundColor: item.MessageRead ? "#111" : "orange",
                    }}
                  />
                </Box>

                {/* ----- loader ----- */}
                {loading && loadingId === item.NotificationId && (
                  <Box className="box-default relative zero-height text-center">
                    <Box
                      className="box-inline bg-black-t90 shadow-default pop-up"
                      sx={{
                        // mt: configEntzy.APP_SPACING_MD2X,
                        p: configEntzy.APP_SPACING_MD,
                        borderRadius: "50%",
                      }}
                    >
                      <ActionLoader size="md" />
                    </Box>
                  </Box>
                )}

                {/* ----- content window ----- */}
                <Box
                  className={
                    item.MessageRead
                      ? "box-default"
                      : "box-default bg-black-t50"
                  }
                  sx={{
                    p: configEntzy.APP_SPACING_MD2X,
                    // backgroundColor: item.MessageRead
                    //   ? "transparent"
                    //   : undefined,
                    color: item.MessageRead ? undefined : "white",
                    borderBottomLeftRadius: configEntzy.BORDER_SIZE_XL,
                    borderBottomRightRadius: configEntzy.BORDER_SIZE_XL,
                  }}
                >
                  {/* ----- content url click wrapper ----- */}
                  <Box
                    className="box-default action-pointer"
                    onClick={() => {
                      handleNavigateEvent(item, internalUrl);
                    }}
                  >
                    {/* ----- message title ----- */}
                    <Box className="box-default">
                      <Typography
                        variant={item.MessageRead ? "subtitle1" : "subtitle2"}
                        noWrap={true}
                      >
                        {item.MessageTitle}
                        {/* {item.MessageRead && (
                          <em>
                            <span>&nbsp;&nbsp;</span>
                            <span>
                              (read)
                            </span>
                          </em>
                        )} */}
                      </Typography>
                    </Box>

                    {/* main message content */}
                    <Box className="box-default">
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-line",
                          overflowWrap: "break-word",
                          pt: configEntzy.APP_SPACING_MD,
                        }}
                      >
                        {item.MessageBody}
                      </Typography>
                    </Box>

                    {/* footer content */}
                    <Box
                      className="box-default"
                      sx={{
                        pt: configEntzy.APP_SPACING_MD2X,
                      }}
                    >
                      <Typography variant="body1">
                        <em>
                          <span>
                            {daysAgo} day{daysAgo === 1 ? "" : "s"} ago
                          </span>
                          {item.MessageRead && (
                            <span>
                              &nbsp;&nbsp;|&nbsp;&nbsp;Read&nbsp;&nbsp;
                              <span style={{ color: "green" }}>
                                <FontAwesomeIcon icon={iconRead} />
                              </span>
                            </span>
                          )}
                        </em>
                      </Typography>
                      {displayUrl && displayUrl.startsWith("@") && (
                        <Box className="box-default relative zero-height">
                          <Box
                            className="box-abs"
                            sx={{
                              bottom: 0,
                              right: 0,
                            }}
                          >
                            <UserAvatar
                              user={{
                                name: displayUrl, // @ is stripped in UserAvatar
                              }}
                              size="sm"
                              avatarOnly
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                    {/* ----- end url click ----- */}
                  </Box>

                  {/* ----- footer controls ----- */}
                  <Box
                    className="box-default text-center"
                    sx={{
                      mt: configEntzy.APP_SPACING_LG,
                      pt: configEntzy.APP_SPACING_MD,
                      pb: configEntzy.APP_SPACING_MD,
                      color: "white",
                      border: item.MessageRead
                        ? configEntzy.BORDER_DASH_DSM
                        : configEntzy.BORDER_DASH_GSM,
                      borderRadius: configEntzy.BORDER_SIZE_XL,
                    }}
                    hidden={
                      (feedView === feeds.messages.id &&
                        !mainContext.state.viewer.trail.editingMessageFeed) ||
                      (feedView === feeds.activity.id &&
                        !mainContext.state.viewer.trail.editingActivityFeed)
                    }
                  >
                    <Box className="box-default">
                      {loading && loadingId === item.NotificationId ? (
                        <TextTag
                          text="Processing..."
                          size="md"
                          bgColor="black"
                          color="gray"
                          shadow={false}
                          rounded={true}
                          edges={true}
                          margin={true}
                        />
                      ) : (
                        <Fragment>
                          <TextTag
                            size="md"
                            text={
                              <span>
                                <span>Mark</span>
                                <span>&nbsp;&nbsp;</span>
                                <span>
                                  {item.MessageRead ? "Unread" : "Read"}
                                </span>
                              </span>
                            }
                            shadow={false}
                            rounded={true}
                            margin={true}
                            edges={true}
                            bgColor={
                              // blue for markunread, green for markread
                              item.MessageRead ? "info.light" : "success.dark"
                            }
                            color={item.MessageRead ? "darkblue" : "darkgreen"}
                            onClick={() =>
                              handleMarkRead(item, !item.MessageRead)
                            }
                          />
                          <TextTag
                            size="md"
                            text={
                              <span>
                                <span>Remove Alert</span>
                              </span>
                            }
                            shadow={false}
                            rounded={true}
                            margin={true}
                            edges={true}
                            bgColor="error.light"
                            color="darkred"
                            onClick={() => handleRemoveConfirm(null, item)}
                          />
                        </Fragment>
                      )}
                    </Box>
                    {/* any info messages */}
                    <Box
                      className="box-default"
                      sx={{
                        pt: configEntzy.APP_SPACING_SM2X,
                        pb: configEntzy.APP_SPACING_XS3X,
                        pl: configEntzy.APP_SPACING_XS3X,
                        pr: configEntzy.APP_SPACING_XS3X,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color={item.MessageRead ? "gray" : "white"}
                        noWrap
                      >
                        <FontAwesomeIcon icon={iconInfo} transform="grow-2" />
                        <span>
                          <span>&nbsp;&nbsp;&nbsp;</span>
                          <span>
                            <span>This alert will auto expire in</span>
                            <span>&nbsp;</span>
                            <span>
                              {configEntzy.NOTIFICATION_EXPIRY_DAYS - daysAgo}
                            </span>
                            <span>&nbsp;</span>
                            <span>day</span>
                            <span>
                              {configEntzy.NOTIFICATION_EXPIRY_DAYS -
                                daysAgo ===
                              1
                                ? ""
                                : "s"}
                            </span>
                          </span>
                          <em></em>
                          <span>&nbsp;&nbsp;&nbsp;</span>
                        </span>
                        <FontAwesomeIcon icon={iconInfo} transform="grow-2" />
                      </Typography>
                    </Box>
                  </Box>

                  {/* ----- end content window ----- */}
                </Box>
              </Box>
            </Box>
          );
        })}

        {mainContext.state.notifications.data.nextToken && (
          <Box className="box-default" sx={{ mt: configEntzy.APP_SPACING_LG }}>
            <MoreButton
              variant="contained"
              size="small"
              type="button"
              color="bright"
              text="More"
              loading={loadingMore}
              onClick={() =>
                handlePullMore(mainContext.state.notifications.data.nextToken)
              }
            />
          </Box>
        )}
      </Box>
    );
  };

  const NotificationEmptyList = (notifyEmptyProps) => {
    return (
      <Box
        className="box-default"
        sx={{
          p: configEntzy.APP_SPACING_SM,
        }}
      >
        <Box
          className="box-default bg-black-t50 shadow-default"
          sx={{
            pt: configEntzy.APP_SPACING_HL,
            pb: configEntzy.APP_SPACING_HL,
            pl: configEntzy.APP_SPACING_MD,
            pr: configEntzy.APP_SPACING_MD,
            borderRadius: configEntzy.BORDER_SIZE_XL,
          }}
        >
          <Container maxWidth="xs">
            <Box
              className="box-default text-paragraph"
              hidden={notifyEmptyProps.category !== "messages"}
            >
              {/* <Typography variant="h1" noWrap={true}>
              <FontAwesomeIcon icon={iconMessages} />
            </Typography> */}
              <Typography
                variant="subtitle2"
                noWrap={true}
                // sx={{
                //   mt: configEntzy.APP_SPACING_MD,
                // }}
              >
                No recent messages
              </Typography>
              <Typography
                variant="subtitle1"
                align="center"
                sx={{
                  mt: configEntzy.APP_SPACING_MD,
                }}
              >
                <em>
                  New messages will appear here when people contact you directly
                  or if you are subscribed to push notifications inside
                  eventuators
                </em>
              </Typography>
            </Box>

            <Box
              className="box-default text-paragraph"
              hidden={notifyEmptyProps.category !== "activity"}
            >
              {/* <Typography variant="h1" noWrap={true}>
                <FontAwesomeIcon icon={iconActvity} />
              </Typography> */}
              <Typography
                variant="subtitle2"
                noWrap={true}
                // sx={{
                //   mt: configEntzy.APP_SPACING_MD,
                // }}
              >
                No recent activity
              </Typography>
              <Typography
                variant="subtitle1"
                align="center"
                sx={{
                  mt: configEntzy.APP_SPACING_MD,
                }}
              >
                <em>
                  Status changes will appear here for eventuators you own or
                  follow or if you have tickets in play
                </em>
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    );
  };

  const NavBar = (navProps) => {
    return (
      <Box
        className="box-default"
        sx={{
          borderRadius: configEntzy.BORDER_SIZE_XL,
          mb: configEntzy.APP_SPACING_MD,
        }}
      >
        <Box
          className="box-default"
          sx={{
            borderRadius: configEntzy.BORDER_SIZE_XL,
          }}
        >
          <Box
            className="box-default half-width"
            sx={{
              pr: configEntzy.APP_SPACING_XS,
            }}
          >
            <BigButton
              size="sm"
              icon={iconOpenDMs}
              iconColor="orange"
              text="Direct Connections"
              onClick={() => {
                props.menuSelectById("direct-messages");
              }}
            />
          </Box>
          <Box
            className="box-default half-width"
            sx={{
              pl: configEntzy.APP_SPACING_XS,
            }}
          >
            <BigButton
              size="sm"
              icon={
                (feedView === feeds.messages.id &&
                  mainContext.state.viewer.trail.editingMessageFeed) ||
                (feedView === feeds.activity.id &&
                  mainContext.state.viewer.trail.editingActivityFeed)
                  ? iconClose
                  : iconEdit
              }
              iconColor="orange"
              text={
                (feedView === feeds.messages.id &&
                  mainContext.state.viewer.trail.editingMessageFeed) ||
                (feedView === feeds.activity.id &&
                  mainContext.state.viewer.trail.editingActivityFeed)
                  ? navProps.count === 0
                    ? "Feed Empty"
                    : "Close"
                  : "Edit Feed"
              }
              disabled={navProps.count === 0}
              onClick={handleEditing}
            />
          </Box>
        </Box>
        <Box
          className="box-default"
          sx={{
            mt: configEntzy.APP_SPACING_SM2X,
            px: configEntzy.APP_SPACING_SM,
          }}
        >
          {loading && loadingId === null && (
            <Box className="box-default relative zero-height text-center">
              <Box
                className="box-inline bg-black-t90 shadow-default pop-up"
                sx={{
                  p: configEntzy.APP_SPACING_MD,
                  borderRadius: "50%",
                }}
              >
                <ActionLoader size="md" />
              </Box>
            </Box>
          )}
          <Box
            className="box-default bg-black-t50"
            sx={{
              p: configEntzy.APP_SPACING_MD,
              borderRadius: configEntzy.BORDER_SIZE_XL,
            }}
          >
            <Box className="box-default">
              <Box className="box-inline left text-left">
                <TextTag
                  text={`All (${navProps.count})`}
                  size="md"
                  bgColor={filterView === "all" ? "purple" : "#333"}
                  color={filterView === "all" ? "white" : "gray"}
                  shadow={false}
                  rounded={true}
                  edges={true}
                  margin={true}
                  noMask={true}
                  onClick={() => {
                    handleFilterView("all");
                  }}
                />
              </Box>
              <Box className="box-inline right">
                <TextTag
                  text={`Unread (${navProps.unread})`}
                  size="md"
                  bgColor={
                    filterView === "unread"
                      ? "purple"
                      : navProps.unread > 0
                      ? "darkred"
                      : "#333"
                  }
                  color={
                    filterView === "unread"
                      ? "white"
                      : navProps.unread > 0
                      ? "white"
                      : "gray"
                  }
                  shadow={false}
                  rounded={true}
                  edges={true}
                  margin={true}
                  noMask={true}
                  onClick={() => {
                    handleFilterView("unread");
                  }}
                />
                <TextTag
                  text={`Read (${navProps.count - navProps.unread})`}
                  size="md"
                  bgColor={filterView === "read" ? "purple" : "#333"}
                  color={filterView === "read" ? "white" : "gray"}
                  shadow={false}
                  rounded={true}
                  edges={true}
                  margin={true}
                  noMask={true}
                  onClick={() => {
                    handleFilterView("read");
                  }}
                />
              </Box>
            </Box>
            {((feedView === feeds.messages.id &&
              mainContext.state.viewer.trail.editingMessageFeed) ||
              (feedView === feeds.activity.id &&
                mainContext.state.viewer.trail.editingActivityFeed)) && (
              <Box className="box-default">
                <Box className="box-default">
                  <Divider
                    sx={{
                      mt: configEntzy.APP_SPACING_MD,
                      mb: configEntzy.APP_SPACING_MD,
                      backgroundColor: "#444",
                    }}
                  />
                </Box>
                <Box className="box-default">
                  {/* <Typography variant="subtitle2" noWrap={true} color="primary">
                  {navProps.text}
                </Typography> */}
                  <TextTag
                    text={navProps.text}
                    size="md"
                    bgColor="transparent"
                    color="orange"
                    shadow={false}
                    rounded={true}
                    edges={true}
                    margin={true}
                    textNoCasing={true}
                    noMask={true}
                  />
                </Box>
                {loading && loadingId === null ? (
                  <Box
                    className="box-inline"
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    <TextTag
                      text="Processing..."
                      size="md"
                      bgColor="black"
                      color="gray"
                      shadow={false}
                      rounded={true}
                      edges={true}
                      margin={true}
                    />
                  </Box>
                ) : (
                  <Box className="box-default">
                    <Box
                      className="box-inline"
                      sx={{ mt: configEntzy.APP_SPACING_MD }}
                    >
                      <TextTag
                        text="Mark all read"
                        size="md"
                        bgColor="success.dark"
                        color="darkgreen"
                        shadow={false}
                        rounded={true}
                        edges={true}
                        margin={true}
                        onClick={() => {
                          handleMarkAllRead(navProps.unreadItems, true);
                        }}
                      />
                    </Box>
                    <Box
                      className="box-inline"
                      sx={{ mt: configEntzy.APP_SPACING_MD }}
                    >
                      <TextTag
                        text="Mark all unread"
                        size="md"
                        bgColor="info.light"
                        color="darkblue"
                        shadow={false}
                        rounded={true}
                        edges={true}
                        margin={true}
                        onClick={() => {
                          handleMarkAllRead(navProps.allItems, false);
                        }}
                      />
                    </Box>
                    <Box
                      className="box-inline"
                      sx={{ mt: configEntzy.APP_SPACING_MD }}
                    >
                      <TextTag
                        text="Remove all"
                        size="md"
                        bgColor="error.light"
                        color="darkred"
                        shadow={false}
                        rounded={true}
                        edges={true}
                        margin={true}
                        onClick={() => {
                          handleRemoveAllConfirm(null, navProps.allItems);
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="box-default">
      <Box
        className="box-default"
        sx={{
          p: configEntzy.APP_SPACING_MD,
        }}
      >
        {/* --------- feed content --------- */}
        <Box className="box-default">
          <Container maxWidth="lg" disableGutters>
            <HeaderMenuTag
              icon={feeds[feedView].icon}
              text={`${feeds[feedView].name} Feed`}
              handleRefresh={handleRefresh}
            />
            <Box className="box-default">
              {mainContext.state.notifications.hydrated && (
                <Box
                  className="box-default"
                  sx={{ pt: configEntzy.APP_SPACING_MD }}
                >
                  <Box
                    className="box-default"
                    hidden={feedView !== feeds.messages.id}
                  >
                    <NavBar
                      text="Your Message Alerts"
                      count={mainContext.state.notifications.data.messagesCount}
                      unread={
                        mainContext.state.notifications.data.messages.filter(
                          (item) => !item.MessageRead,
                        ).length
                      }
                      allItems={mainContext.state.notifications.data.messages}
                      unreadItems={mainContext.state.notifications.data.messages.filter(
                        (item) => !item.MessageRead,
                      )}
                      position="left"
                    />
                    <Box
                      className="box-default"
                      sx={{
                        borderRadius: configEntzy.BORDER_SIZE_XL,
                      }}
                    >
                      {mainContext.state.notifications.data.messagesCount >
                      0 ? (
                        <NotificationList
                          notifications={
                            filterView === "all"
                              ? mainContext.state.notifications.data.messages
                              : filterView === "unread"
                              ? mainContext.state.notifications.data.messages.filter(
                                  (item) => !item.MessageRead,
                                )
                              : mainContext.state.notifications.data.messages.filter(
                                  (item) => item.MessageRead,
                                )
                          }
                        />
                      ) : (
                        <NotificationEmptyList category="messages" />
                      )}
                    </Box>
                  </Box>
                  <Box
                    className="box-default"
                    hidden={feedView !== feeds.activity.id}
                  >
                    <NavBar
                      text="Your Activity Alerts"
                      count={mainContext.state.notifications.data.activityCount}
                      unread={
                        mainContext.state.notifications.data.activity.filter(
                          (item) => !item.MessageRead,
                        ).length
                      }
                      allItems={mainContext.state.notifications.data.activity}
                      unreadItems={mainContext.state.notifications.data.activity.filter(
                        (item) => !item.MessageRead,
                      )}
                      position="right"
                    />
                    <Box
                      className="box-default"
                      sx={{
                        borderRadius: configEntzy.BORDER_SIZE_XL,
                      }}
                    >
                      {mainContext.state.notifications.data.activityCount >
                      0 ? (
                        <NotificationList
                          notifications={
                            filterView === "all"
                              ? mainContext.state.notifications.data.activity
                              : filterView === "unread"
                              ? mainContext.state.notifications.data.activity.filter(
                                  (item) => !item.MessageRead,
                                )
                              : mainContext.state.notifications.data.activity.filter(
                                  (item) => item.MessageRead,
                                )
                          }
                        />
                      ) : (
                        <NotificationEmptyList category="activity" />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
      {/* dialog popup */}
      <Box className="box-default">
        <ActionCheck
          dialog={dialog.visible}
          title={dialog.title}
          description={dialog.description}
          cancel={dialog.cancel}
          confirm={dialog.confirm}
        />
      </Box>
    </Box>
  );
}

export default MainFeed;
